import * as React from 'react'
import dayjs from 'dayjs'
import TextField from '@mui/material/TextField'
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  UseFormSetValue,
  UseFormTrigger,
  useWatch
} from 'react-hook-form'
import {
  Autocomplete,
  FormHelperText,
  Grid,
  Snackbar,
  SnackbarContent,
  styled
} from '@mui/material'
import { FormValues } from '../../lead-form/LeadForm'
import isBetween from 'dayjs/plugin/isBetween'
dayjs.extend(isBetween)

interface Props {
  control: Control<FormValues>
  errors: FieldErrors<FormValues>
  trigger: UseFormTrigger<FormValues>
  setValue: UseFormSetValue<FormValues>
}

const minDate = dayjs().subtract(18, 'year')
const minYear = minDate.year()

const maxDate = dayjs().subtract(85, 'year')
const maxYear = maxDate.year()

const ErrorSnackbarContent = styled(SnackbarContent)(({ theme }) => ({
  backgroundColor: theme.palette.error.main
}))

const DOBField = ({
  control: typedControl,
  errors,
  trigger,
  setValue
}: Props) => {
  const control = typedControl as unknown as Control<FieldValues>

  const [days, setDays] = React.useState<string[]>(
    Array.from(Array(31).keys(), (_, index) => index + 1).map(String)
  )

  const [snackbarOpen, setSnackbarOpen] = React.useState(false)
  const month = useWatch({
    control,
    name: 'dobData.month'
  })

  const year = useWatch({
    control,
    name: 'dobData.year'
  })

  const day = useWatch({
    control,
    name: 'dobData.day'
  })

  React.useEffect(() => {
    if (!year || !month || !month.id) {
      setDays(Array.from(Array(31).keys(), (_, index) => index + 1).map(String))
      return
    }
    let d = dayjs().date(1).year(year).month(month.id)
    const newDays = Array.from(
      Array(
        dayjs(d).daysInMonth() // 31
      ).keys(),
      (_, index) => index + 1
    ).map(String)
    setDays(newDays)

    if (!newDays.includes(day)) {
      setValue('dobData.day', null)
      trigger('dobData.day')
    } else {
      d = d.date(day)
    }

    if (!d.isBetween(minDate, maxDate, 'day', '[]')) {
      setSnackbarOpen(true)
      setValue('dobData.year', null, { shouldValidate: true })
    }
  }, [month, year, day, setValue, trigger])

  const missingParts = [
    errors?.dobData?.month && 'Month',
    errors?.dobData?.day && 'Day',
    errors?.dobData?.year && 'Year'
  ].filter(Boolean) as string[]

  const dobErrorMessage =
    missingParts.length === 3
      ? 'Date of birth is required'
      : missingParts.length > 0
        ? `${missingParts.join(' and ')} ${
            missingParts.length > 1 ? 'are' : 'is'
          } required`
        : null

  return (
    <Grid container spacing={3} direction='row'>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name='dobData.month'
          control={control}
          render={({ field }) => (
            <Autocomplete
              {...field}
              size='medium'
              classes={{ root: 'bg-white', inputRoot: 'rounded-none' }}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, v) => {
                field.onChange(v)
              }}
              options={dayjs.Ls['en'].months!.map((v, i) => ({
                label: v,
                id: String(i)
              }))}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.label}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Month'
                  variant='standard'
                  required
                  InputProps={{
                    ...params.InputProps,
                    style: { paddingRight: '0px' },
                    endAdornment: null
                  }}
                  error={!!errors?.dobData?.month}
                />
              )}
            />
          )}
        ></Controller>
      </Grid>
      <Grid size={{ xs: 12, sm: 3 }}>
        <Controller
          name='dobData.day'
          control={control}
          render={({ field }) => (
            <Autocomplete
              {...field}
              size='medium'
              classes={{
                root: 'bg-white',
                inputRoot: 'rounded-none',
                paper: 'min-w-[70px]'
              }}
              onChange={(_, data) => field.onChange(data)}
              options={days}
              autoSelect={true}
              freeSolo
              // freeSolo defaults clearOnBlur to false, which also stops the
              // input text from resetting when the value is cleared in code
              clearOnBlur
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Day'
                  variant='standard'
                  required
                  InputProps={{
                    ...params.InputProps,
                    style: { paddingRight: '0px' },
                    endAdornment: null
                  }}
                  error={!!errors?.dobData?.day}
                />
              )}
            />
          )}
        ></Controller>
      </Grid>
      <Grid size={{ xs: 12, sm: 3 }}>
        <Controller
          name='dobData.year'
          control={control}
          render={({ field }) => (
            <Autocomplete
              {...field}
              freeSolo
              clearOnBlur
              clearIcon={false}
              fullWidth={true}
              classes={{
                root: 'bg-white',
                inputRoot: 'rounded-none',
                paper: 'min-w-[90px]'
              }}
              onChange={(_, data) => field.onChange(data)}
              options={Array.from(
                { length: minYear - maxYear + 1 },
                (_, i) => minYear - i
              ).map(String)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant='standard'
                  label='Year'
                  fullWidth={true}
                  required
                  InputProps={{
                    ...params.InputProps,
                    style: { paddingRight: '0px' },
                    endAdornment: null
                  }}
                  error={!!errors?.dobData?.year}
                />
              )}
            />
          )}
        ></Controller>
      </Grid>

      {/* v7 Grid spacing is a flex gap, so offset it to keep the error
          text right under the fields */}
      <Grid size={12} sx={{ mt: '-20px' }}>
        <FormHelperText error sx={{ minHeight: '1.25em', mt: 0 }}>
          {dobErrorMessage || ' '}
        </FormHelperText>
      </Grid>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        key={'Snackbar'}
      >
        <ErrorSnackbarContent message='To proceed, your date of birth must indicate that you are between 18 and 85 years old.' />
      </Snackbar>
    </Grid>
  )
}

DOBField.displayName = 'DOBField'

export default DOBField
